import { Checkbox, CheckboxField, Label, Option, Select, Textarea, TextInput } from '@d-edge/overnight-hotelier-react'
import { useState, type FormEvent, type ReactNode } from 'react'
import { useSettingsRowMutations } from '@/api/queries'
import type { FieldSchema, SettingsRow, SettingsTableDescription, WorkflowStep } from '@/api/types'
import { Button } from '@/components/ui/Button'
import { Field } from '@/components/ui/Field'
import { Modal } from '@/components/ui/Modal'
import { useToast } from '@/components/ui/Toast'
import { fieldLabel, normaliseRow, validateRow } from './fieldValues'
import styles from './settings.module.css'
import { WorkflowEditor } from './WorkflowEditor'

interface RowFormProps {
  table: SettingsTableDescription
  row: SettingsRow
  isNew: boolean
  onClose: () => void
}

function Check({ id, checked, onChange, children }: { id?: string; checked: boolean; onChange: (checked: boolean) => void; children: ReactNode }) {
  return (
    <CheckboxField>
      <Checkbox id={id} checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <Label>{children}</Label>
    </CheckboxField>
  )
}

const sectionId = (index: number) => `settings-section-${index}`

function jumpTo(index: number) {
  document.getElementById(sectionId(index))?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

/** Generic editor driven by the table schema; key fields are frozen once a row exists. */
export function RowForm({ table, row: initial, isNew, onClose }: RowFormProps) {
  const [row, setRow] = useState<SettingsRow>(initial)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const { create, update } = useSettingsRowMutations(table.key)
  const toast = useToast()
  const fields = table.sections.flatMap((s) => s.fields)
  const busy = create.isPending || update.isPending
  const set = (name: string, value: unknown) => setRow((r) => ({ ...r, [name]: value }))
  const title = fields.filter((f) => f.isKey).map((f) => String(row[f.name] ?? '')).filter(Boolean).join(' · ')

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    const problems = validateRow(fields, row)
    setErrors(problems)
    if (Object.keys(problems).length > 0) return
    try {
      const payload = normaliseRow(fields, row)
      if (isNew) await create.mutateAsync(payload)
      else await update.mutateAsync(payload)
      toast.notify(`${table.name}: ${title || 'row'} saved`, 'success')
      onClose()
    } catch (err) {
      toast.error(err, 'Save failed')
    }
  }

  const control = (field: FieldSchema): ReactNode => {
    const id = `row-${field.name}`
    const value = row[field.name]
    const locked = field.isKey && !isNew
    const invalid = Boolean(errors[field.name])
    switch (field.type) {
      case 'textarea':
        return <Textarea rows={3} fullWidth resize="vertical" value={String(value ?? '')} invalid={invalid} onChange={(e) => set(field.name, e.target.value)} />
      case 'integer':
        return (
          <input
            id={id}
            type="number"
            value={value === null || value === undefined ? '' : String(value)}
            disabled={locked}
            onChange={(e) => set(field.name, e.target.value)}
            className={`htl-text-input htl-text-input--full-width ${invalid ? styles.invalid : ''}`}
          />
        )
      case 'boolean':
        return (
          <Check id={id} checked={Boolean(value)} onChange={(checked) => set(field.name, checked)}>
            {value ? 'yes' : 'no'}
          </Check>
        )
      case 'enum':
        return (
          <Select fullWidth value={String(value ?? '')} disabled={locked} invalid={invalid} onChange={(next) => set(field.name, next)}>
            {(field.values ?? []).map((v) => (
              <Option key={v} value={v} label={v === '' ? '–' : v} />
            ))}
          </Select>
        )
      case 'menum': {
        const selected = new Set(Array.isArray(value) ? (value as string[]) : [])
        return (
          <div className={styles.checkGroup}>
            {(field.values ?? []).map((v) => (
              <Check key={v} checked={selected.has(v)} onChange={(checked) => set(field.name, checked ? [...selected, v] : [...selected].filter((s) => s !== v))}>
                {v}
              </Check>
            ))}
          </div>
        )
      }
      case 'checkbox': {
        const flags = (value as Record<string, boolean> | undefined) ?? {}
        return (
          <div className={styles.checkGroup}>
            {(field.checkboxKeys ?? []).map((k) => (
              <Check key={k} checked={Boolean(flags[k])} onChange={(checked) => set(field.name, { ...flags, [k]: checked })}>
                {k}
              </Check>
            ))}
          </div>
        )
      }
      case 'workflow':
        return <WorkflowEditor value={(value as WorkflowStep[] | undefined) ?? []} requesterTypes={field.values ?? []} proxyProducts={field.checkboxKeys ?? []} onChange={(steps) => set(field.name, steps)} />
      default:
        return (
          <TextInput
            fullWidth
            value={String(value ?? '')}
            disabled={locked}
            invalid={invalid}
            placeholder={field.type === 'time' ? '00:00:00' : undefined}
            onChange={(e) => set(field.name, e.target.value)}
          />
        )
    }
  }

  return (
    <Modal
      title={isNew ? `${table.name} · new row` : `${table.name} · ${title}`}
      onClose={onClose}
      size="xl"
      footer={
        <>
          <Button onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="primary" type="submit" form="settings-row-form" loading={busy}>Save</Button>
        </>
      }
    >
      <form id="settings-row-form" className={styles.sections} onSubmit={submit}>
        {table.sections.length > 2 && (
          <nav className={styles.sectionNav} aria-label="Jump to a section">
            {table.sections.map((section, index) => (
              <button key={section.name} type="button" className={styles.navChip} onClick={() => jumpTo(index)}>
                {section.name}
              </button>
            ))}
          </nav>
        )}
        {table.sections.map((section, index) => (
          <section key={section.name} id={sectionId(index)} className={styles.section}>
            <header className={styles.sectionHeader}>
              <h3 className={styles.sectionTitle}>{section.name}</h3>
              {section.description && <p className={styles.sectionDescription}>{section.description}</p>}
            </header>
            <div className={styles.sectionBody}>
              <div className={styles.fields}>
                {section.fields.map((field) => (
                  <div key={field.name} className={field.type === 'workflow' || field.type === 'textarea' ? styles.wide : undefined}>
                    <Field label={fieldLabel(field)} htmlFor={field.type === 'integer' || field.type === 'boolean' ? `row-${field.name}` : undefined} required={field.isRequired} hint={field.message} error={errors[field.name]}>
                      {control(field)}
                    </Field>
                  </div>
                ))}
              </div>
            </div>
          </section>
        ))}
      </form>
    </Modal>
  )
}
